import React, { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Modal } from 'antd'
import {
  createSession,
  getCardID,
  getCardStages,
  getDataLoading,
  getVisibleFormData,
  getCardStagesItems,
  getCardCurrentStage,
  getCardStagesHistory,
} from '../../../selectors'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { updateFormData } from '../../../../../../store/claim_request/action-creators'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import { HStack } from '../../../../../../ui/Stack'
import { HSteps } from '../../../../../../ui/Steps'
import { hasPermission } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

interface FormHeaderProps {
  showTitle?: boolean
}

export const FormHeader: React.FC<FormHeaderProps> = memo(() => {
  const dispatch = useAppDispatch()
  const id = useSelector(getCardID)
  const stageObj = useSelector(getCardStages)
  const { sessionData } = useSelector(createSession)
  const dataIsLoading = useSelector(getDataLoading)
  const visibleFormData = useSelector(getVisibleFormData)
  const stagesItems = useSelector(getCardStagesItems)
  const currentStage = useSelector(getCardCurrentStage)
  const stagesHistory = useSelector(getCardStagesHistory)

  const handleToClaim = useCallback(() => {
    if (!id) return
    const data = visibleFormData
    if (data) {
      const isRequiredFields = isFormDataRequired(data.resultObject, data.resultSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      } else {
        dispatch(updateFormData(id, { stage: 'approved' }))
      }
    }
  }, [id, visibleFormData, dispatch])

  const buttonsDisabled = useMemo(() => {
    if (dataIsLoading || !id) {
      return true
    }
    const hasAccess = hasPermission(sessionData?.permissionsWithScope, Permissions.ACCESS_CLAIMS)
    if (stageObj?.items !== undefined && stageObj?.current !== undefined && hasAccess) {
      return stageObj?.items?.[stageObj?.current]?.code !== 'new'
    }
    return !hasAccess
  }, [dataIsLoading, stageObj, sessionData, id])

  const actionsConfig = useMemo(
    (): CardActionConfig[] => [
      {
        key: 'to-claim',
        label: 'Принять',
        type: 'primary',
        onClick: handleToClaim,
        isPrimary: true,
        disabled: buttonsDisabled || id === 'new',
        condition: true,
        order: 1,
      },
    ],
    [handleToClaim, buttonsDisabled, id],
  )

  const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

  return (
    <HStack gap={'8'} className={'mapWrapper'} wrap>
      <div>
        {stagesItems && (
          <HSteps
            items={Object.values(stagesItems)}
            current={currentStage}
            history={stagesHistory}
          />
        )}
      </div>
      <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
    </HStack>
  )
})

FormHeader.displayName = 'FormHeader'
